import { SurveyMediaChecklistOptions } from '@airship-platform/airship-patterns';
import { FlexBox } from '@airship-platform/jam-web/components';
import { CameraIcon } from '@airship-platform/jam-web/icons';
import { zodResolver } from '@hookform/resolvers/zod';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Link from '@mui/material/Link';
import Typography from '@mui/material/Typography';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link as RouterLink } from 'react-router';
import { z } from 'zod';
import { useJobImagesProvider } from '../../../../data-access/jobImages';
import MediaCapture from '../media-section/MediaCapture';
import MediaChecklistModal from '../media-section/MediaChecklistModal';
import MediaList from '../media-section/MediaList';

type MediaSectionProps = {
  checklistOptions: SurveyMediaChecklistOptions;
  defaultValues: MediaSectionInputs;
  formId?: string;
  onSubmit: (data: MediaSectionInputs) => Promise<void>;
};

const MediaSectionSchema = z.object({
  indoor: z.string().array(),
  outdoor: z.string().array(),
});

export type MediaSectionInputs = z.infer<typeof MediaSectionSchema>;

const MediaSection: React.FC<MediaSectionProps> = ({
  checklistOptions,
  defaultValues,
  formId,
  onSubmit,
}) => {
  const [isChecklistModalOpen, setIsChecklistModalOpen] = useState(false);
  const { availableImages, isLoading } = useJobImagesProvider();

  const { handleSubmit, setValue, getValues } = useForm<MediaSectionInputs>({
    resolver: zodResolver(MediaSectionSchema),
    defaultValues,
  });

  const onChecklistModalClose = ({
    indoor,
    outdoor,
  }: SurveyMediaChecklistOptions) => {
    setValue('indoor', indoor);
    setValue('outdoor', outdoor);
    setIsChecklistModalOpen(false);
  };

  return (
    <Box
      component={'form'}
      id={formId}
      onSubmit={handleSubmit(onSubmit)}
      autoComplete="off"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 3,
        width: '100%',
      }}
    >
      <Typography variant={'body2'}>
        <strong>Pro Tip:</strong> Use the camera app on your device to capture
        photos before uploading to ensure you maintain a copy.
      </Typography>

      <Typography variant={'body2'}>
        <Link onClick={() => setIsChecklistModalOpen(true)}>See checklist</Link>{' '}
        for guidance on what photos to capture.
      </Typography>

      <MediaCapture />

      <Typography variant="body2" sx={{ textAlign: 'center' }}>
        Or
      </Typography>

      <FlexBox
        alignItems="center"
        justifyContent="space-between"
        gap={2}
        border={(theme) => `1px solid ${theme.palette.divider}`}
        borderRadius={1}
        padding={2}
      >
        <Typography variant="body2">
          Browse previously captured photos and videos from ServiceTitan to add.
        </Typography>
        <Button
          color="softGold"
          size="small"
          component={RouterLink}
          startIcon={<CameraIcon />}
          to="job-media"
          sx={{ whiteSpace: 'nowrap' }}
          disabled={isLoading}
        >
          Browse media ({availableImages.length})
        </Button>
      </FlexBox>

      <MediaList />

      <MediaChecklistModal
        open={isChecklistModalOpen}
        checklistOptions={checklistOptions}
        selectedChecklist={getValues()}
        onClose={onChecklistModalClose}
      />
    </Box>
  );
};

export default MediaSection;
